import { useDispatch, useSelector } from "react-redux";
import { useCallback, useEffect, useMemo } from "react";
import { useUnitMode } from "../../../ImperialMetricData/useUnitMode";
import { TreeTable, type TreeTableColumnDef } from "../../../common/components/TreeTable/TreeTable";
import { type RootState } from "../../../redux/store";
import { type SRPSeriesTreeNode } from "../../../Search/models";
import { fetchNavigationSeriesTree } from "../../../Header/api/fetchNavigationSeriesTree";
import { transformSeriesTreeNode } from "../../../Search/transformers/SearchResultDataTransformer";
import { loadNavigationSeriesTreeData, navigationToggleSeriesTreeNodeAction } from "../../../Header/redux/actions";
import CenterContents from "../../../common/components/CenterContents";
import CircleSpinner from "../../../common/components/CircleSpinner/CircleSpinner";
import { Link, LinkType } from "../../../common/components/Link";
import { resolveUrl } from "../../../utils/url/urlResolver";
import { Trans } from "react-i18next";
import { FROM_SERIES_TREE_PARAM } from "../../../Search/utils/searchUtils";
import { UnitMode } from "../../../ImperialMetricData/redux/UnitMode";

import { isNonNull } from "~/utils/reducers";
import ImperialFraction from "~/common/components/ImperialFraction";
import classNames from "classnames";

interface Props {
  className?: string;
}

export const NavigationSeriesView = ({ className }: Props) => {
  const seriesTreeData = useSelector((state: RootState) => state.navigation.seriesTree.data.data);
  const isDataLoaded = useSelector((state: RootState) => state.navigation.seriesTree.data.state) === "loaded";
  const dispatch = useDispatch();
  useEffect(() => {
    if (!isDataLoaded) {
      fetchNavigationSeriesTree()
        .then((data) => {
          const seriesTreeData = (data ?? []).map((node) => transformSeriesTreeNode(node, null)).filter(isNonNull);
          dispatch(loadNavigationSeriesTreeData(seriesTreeData));
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }, []);

  const unitMode = useUnitMode();
  const columnConfig: TreeTableColumnDef<SRPSeriesTreeNode>[] = useMemo(() => {
    const getShaftLabel = (node: SRPSeriesTreeNode) => {
      let min, max;
      if (unitMode === UnitMode.Imperial) {
        if (window.SCHA.feature.inchToFraction) {
          min = node.minImperialDiameter && <ImperialFraction value={node.minImperialDiameter} />;
          max = node.maxImperialDiameter && <ImperialFraction value={node.maxImperialDiameter} />;
        } else {
          min = node.minImperialDiameter;
          max = node.maxImperialDiameter;
        }
      } else {
        min = node.minDiameter;
        max = node.maxDiameter;
      }
      if (!min || !max) {
        return "";
      }
      return (
        <div className={classNames(node.isImperialConverted && unitMode === UnitMode.Imperial && "f-info-color")}>
          {min} - {max}
        </div>
      );
    };

    return [
      {
        title: (
          <strong>
            <Trans i18nKey={"seriesTree.header.productGroup"} />
          </strong>
        ),
        width: "50%",
        render: (node) => (
          <>
            <Link
              href={resolveUrl(`/plp/${getPlpCode(node.categoryCode, node.isSeries)}?${FROM_SERIES_TREE_PARAM}=true`)}
              variant={LinkType.NAVIGATION}
            >
              {node.name}
            </Link>
            <span> ({node.count})</span>
          </>
        ),
      },
      {
        title: (
          <strong>
            <Trans
              i18nKey={"seriesTree.header.shaftDiameter"}
              values={{ unit: unitMode === UnitMode.Metric ? "mm" : "in" }}
            />
          </strong>
        ),
        width: "50%",
        render: getShaftLabel,
      },
    ];
  }, [unitMode]);

  const onToggleExpand = useCallback(
    (nodeId: string, isExpanded: boolean) => dispatch(navigationToggleSeriesTreeNodeAction(nodeId, isExpanded)),
    [],
  );

  if (!seriesTreeData) {
    return (
      <CenterContents className={className}>
        <CircleSpinner />
      </CenterContents>
    );
  }

  return (
    <div className={className}>
      <TreeTable data={seriesTreeData} colDefs={columnConfig} onToggleExpand={onToggleExpand} />
    </div>
  );
};

const getPlpCode = (fullCategoryCode: string, isSeries: boolean) => {
  const parts = fullCategoryCode.split("/");
  const length = parts.length;
  return isSeries ? parts[length - 2] + "/" + parts[length - 1].substring(2) : parts[length - 1];
};
